import { Box } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
	CoreButton,
	CoreDatePicker,
	CoreIconButton,
	CoreModal,
	CorePaper,
	CoreTextField,
	coreToast,
	StepConfig,
} from "@verity/components/core";
import type { DeltaStatic } from "quill";
import { useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import InviteeInfoTable from "./InviteeInfoTable";
import {
	EditQuizWrapperRef,
	ExemptedStudent,
	ThemeWrapper,
	api,
	StandAloneExemptedStudentsContext,
	useMobileLayout,
	EditQuizStepper,
	EditQuizFooter,
	EditQuizStep,
	getEditQuizStepName,
	useEditQuizButton,
} from "@verity/components";
import { AssessmentInfo } from "@verity/api-client/types";
import { intIdToUuid } from "../../../../helpers/uuid";
import InstructionView from "../../../../standalone/components/InstructionView";
import i18n from "../../../../i18n";
import apiClient from "../../../../helpers/api-client";
import { PortalControllerContext } from "../../../context";
import styles from "./styles.module.css";
import XIcon from "./x-icon.svg?react";
import EditAssessmentOptionsPage from "../editAssessmentOptionPage";
import { ApiClientError } from "@verity/api-client";
import { validateEmail } from "../../../../helpers/email";
import { getBrowserName } from "@verity/extension/platform";
import { Match } from "effect";
import InstructionEditor from "../../../../quill-editor/InstructionEditor";

function ErrorModal({ onClose }: { onClose: () => void }) {
	return (
		<CoreModal
			title={i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.indexTSX.emptyTable")}
			open
			onClose={onClose}
			buttons={
				<Box>
					<CoreButton color="secondary" onClick={onClose}>
						{i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.indexTSX.close")}
					</CoreButton>
				</Box>
			}
			widthFixed
			minWidth="min(720px, 100%)"
			heightFixed
			minHeight="249px"
		>
			<div style={{ color: "#6B6B6B", fontSize: 15, marginTop: "4px" }}>
				{i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.indexTSX.descriptionBox")}
			</div>
		</CoreModal>
	);
}

export default function CreateStandaloneAssessmentSection({
	instituteUuid,
	courseUuid,
	onFinish,
}: {
	instituteUuid: string;
	courseUuid: string;
	onFinish: () => void;
}) {
	const { portalContext } = useContext(PortalControllerContext);
	const { lms, instituteIntegrationHostname, lmsProvidedCourseId } = portalContext;

	const [title, setTitle] = useState("");
	const [dueDate, setDueDate] = useState<Date | null>(null);
	const [description, setDescription] = useState("");
	const [titleHelperTest, setTitleHelperTest] = useState("");

	const [columns, setColumns] = useState(["Email", "First Name", "Last Name"]);
	const [rows, setRows] = useState<Record<string, string>[]>([]);
	const [showErrorDialog, setShowErrorDialog] = useState(false);

	const [instruction, setInstruction] = useState("");
	const [delta, setDelta] = useState<DeltaStatic>(
		JSON.parse(
			'{"ops":[{"insert":"Introduction"},{"attributes":{"header":3},"insert":"\\n"},{"insert":"Welcome to the online assessment for [course/program name]! The assessment is designed to evaluate your knowledge and suitability for our program. \\n\\nThe assessment will be conducted in an online proctored environment with advanced security features enabled to provide a fair and standardized evaluation. Please ensure you take the assessment from a safe and secure location using a desktop/laptop (with a webcam) and a reliable internet connection. \\n\\nTo begin, please carefully read and follow the instructions below to ensure you understand what is to be expected before starting the assessment. \\n\\nStep 1"},{"attributes":{"header":3},"insert":"\\n"},{"attributes":{"color":"#757575"},"insert":"If steps are required, enter the first step here.Insert the first step here"},{"insert":"\\n\\nStep 2"},{"attributes":{"header":3},"insert":"\\n"},{"attributes":{"color":"#757575"},"insert":"Enter the second step here if required and add any additional steps."},{"insert":"\\n\\nSubmission"},{"attributes":{"header":3},"insert":"\\n"},{"attributes":{"color":"#757575"},"insert":"Enter any steps the invitee must take before submitting their assessment."},{"insert":"\\n\\n\\n"}]}',
		) as DeltaStatic,
	);

	const [shouldShowPreview, setShouldShowPreview] = useState(false);
	const rightSideRef = useRef<HTMLDivElement>(null);

	const titleRef = useRef<HTMLInputElement>(null);
	const datePickerRef = useRef<HTMLInputElement>(null);
	useEffect(() => {
		if (titleHelperTest !== "" && titleRef.current) {
			titleRef.current?.focus();
		}
	}, [titleHelperTest]);

	const isMobileLayout = useMobileLayout();
	const [step, setStep] = useState(0);
	const [shouldShowAddTestTakerTable, setShouldShowAddTestTakerTable] = useState(false);
	const [completedSteps, setCompletedSteps] = useState<number[]>([]); // Need to keep track of steps since progress bar is non linear
	const [pendingPageName, setPendingPageName] = useState<EditQuizStep | null>(null);

	const modalBodyRef = useRef<HTMLDivElement>(null);

	const [isCSVParsed, setCSVParsed] = useState(false);
	const [savedAssessmentInfo, setSavedAssessmentInfo] = useState<AssessmentInfo | undefined>();
	const standaloneAssessmentEditQuizPageRef = useRef<EditQuizWrapperRef>(null);

	const { backButton, nextButton, saveButton, saveProfileButton, buttonHandlers } = useEditQuizButton();
	const [isFinalEditQuizPage, setIsFinalEditQuizPage] = useState(false);
	const [uploadedExemptedStudentsMap, setUploadedExemptedStudentsMap] = useState<Map<number, ExemptedStudent>>(
		new Map(),
	);

	const leftSideRef = useRef<HTMLDivElement>(null);
	const [isSaving, setIsSaving] = useState(false);

	// whenever step changes, if we're on 0 or 1, focus the modal body. This is so focus doesn't stay on next button
	useEffect(() => {
		if (step <= 1 && modalBodyRef.current) {
			modalBodyRef.current.focus();
		}
	}, [step]);

	// Wait for ref to become not null
	useEffect(() => {
		if (pendingPageName === null) return;

		if (standaloneAssessmentEditQuizPageRef.current) {
			standaloneAssessmentEditQuizPageRef.current.goToPage(pendingPageName);

			setPendingPageName(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pendingPageName, standaloneAssessmentEditQuizPageRef.current]);

	const [enforcedProfilesOnlyEditQuizPage, setEnforcedProfilesOnlyEditQuizPage] = useState(false);
	// For updating when a step is completed, since steps 3-6 are handled by editQuiz
	useEffect(() => {
		setCompletedSteps((prev) => {
			// If we moved forward, ensure all indices below 'step' are completed
			const highestStepReached = step;
			const currentlyCompleted = new Set(prev);

			for (let i = 0; i < highestStepReached; i++) {
				currentlyCompleted.add(i);
			}

			// Only update state if the count actually changed to avoid extra renders
			if (currentlyCompleted.size === prev.length) return prev;

			return Array.from(currentlyCompleted).sort((a, b) => a - b);
		});
	}, [step]);

	const saveQuiz = async () => {
		// if a standalone assessment hasn't been created, create one.
		if (!lmsProvidedCourseId) {
			console.error(`context missing: lmsProvidedCourseId=${lmsProvidedCourseId}`);
			return;
		}

		const defaultProfile = await api.getDefaultProctoringProfile();
		const quizInfo: AssessmentInfo = {
			lms,
			hostname: instituteIntegrationHostname,
			courseId: lmsProvidedCourseId,
			quizId: savedAssessmentInfo?.quizId ?? crypto.randomUUID().replaceAll("-", ""),
			quizInfo: {
				name: title,
			},
		};
		if (!quizInfo) {
			console.error("cannot find quizInfo");
		}

		if (!savedAssessmentInfo) {
			try {
				setIsSaving(true);
				await api.saveProctoringAssessmentConfiguration({
					assessmentInfo: quizInfo,
					proctoringAssessmentConfiguration: {
						enabled: true,
					},
					proctoringOption: defaultProfile?.proctoringOption ?? { isExtension: true },
					profileId: defaultProfile?.id,
					// create a quiz with dummy standaloneOptions to designate the quiz as a standalone quiz, not an lms quiz.
					// The dummy standaloneOptions will be replaced when the instructor confirms the proctoring options in the last step.
					// If the last step is not reached, the quiz will be deleted.
					standaloneOptions: {
						studentInfoEntries: [],
						instructionTemplate: "",
						emailBody: "",
						expiryDate: new Date(),
						exemptedEmails: [],
					},
				});

				setSavedAssessmentInfo(quizInfo);
				setIsSaving(false);
			} catch (e) {
				console.error(e);
			}
		}
		setSavedAssessmentInfo(quizInfo);
	};

	// Platform/Configuration is handled by EditQuiz
	const fixedSteps: EditQuizStep[] = useMemo(() => {
		return ["assessmentDetails", "inviteTestTakers", "profile", "accommodations", "confirmation"] as EditQuizStep[];
	}, []);

	const standAloneStepConfig = useMemo(() => {
		const config: StepConfig<EditQuizStep>[] = [];

		fixedSteps.forEach((pageName) => {
			config.push({
				key: pageName,
				label: getEditQuizStepName(pageName),
			});
		});

		return config;
	}, [fixedSteps]);

	const [datePickerError, setDatePickerError] = useState<string | undefined>(undefined);

	// This function validates the due date and sets the datePickerError. Returns true if no error
	const validateDueDate = (date: Date | null) => {
		let message: string | undefined;

		if (!date) {
			// Empty Date
			message = i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.indexTSX.dateNoEmpty");
		} else if (!(date instanceof Date) || isNaN(date.getTime())) {
			// Incomplete Date
			message = i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.indexTSX.incompleteDate");
		} else if (date.getTime() < new Date().setHours(0, 0, 0, 0)) {
			// the instructor can set today as the dueDate.
			// the dueDate sent to the back end will be dueDate.setHours(23, 59, 59, 999)
			message = i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.indexTSX.invalidDate");
			coreToast.error(i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.indexTSX.datePassed"), {
				autoClose: 2500,
			});
		}

		setDatePickerError(message);
		return !message;
	};

	const validateEmails = (rows: Record<string, string>[]): boolean => {
		return rows.every((row) => {
			const email = row["Email"];
			return email && validateEmail(email);
		});
	};

	// Validation for step 2
	const validateStep2 = () => {
		const doesExistEmptyRow = rows.length <= 0 || rows.some((row) => !row["Email"]?.trim());
		const areAllEmailsValid = validateEmails(rows);

		return !(doesExistEmptyRow || !areAllEmailsValid);
	};

	type NavigationSource = "next" | "back" | "stepper";

	// For the CoreStepper. since it expects a function of number -> void
	async function stepperHandler(targetStep: number) {
		await handleStepChange(targetStep, "stepper");
	}

	/**  Extracted step validation into function. Pass the step the user wants to go to. Pass if user clicked on step, next, or back button, as that will trigger different handlers.
     Will use setStep at the end and will either go to targetStep, or remain on current step depending on validation code.
     */
	async function handleStepChange(targetStep: number, source: NavigationSource) {
		// If we are at Step 2 or higher, and next/back button is clicked, delegate to next/back button handler because they handle configuration page logic
		if (step >= 2 && source != "stepper") {
			standaloneAssessmentEditQuizPageRef.current?.[source === "next" ? "nextButtonHandler" : "backButtonHandler"]();
			return; // skip the rest of the logic, since the handlers, handles it
		}

		// Rest of the validation is for steps 0 and 1
		let newStep = targetStep; // Used variable instead of returning early if there are issues, because that's what old navigation code did

		// Validate first page before going anywhere
		if (step === 0) {
			if (title === "") {
				setTitleHelperTest(i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.indexTSX.nameNoEmpty"));
				newStep = step;
			} else if (!validateDueDate(dueDate)) {
				datePickerRef.current?.focus();
				return;
			}
		}

		// Make sure everything is validated before jumping to steps handled by ref
		if (targetStep >= 2) {
			// These errors will show an error dialog
			if (!validateStep2()) {
				setShowErrorDialog(true);
				setStep(step);
				return;
			}

			// These errors don't show an error dialog
			if (!title || !validateDueDate(dueDate)) {
				setStep(step);
				return;
			}

			await saveQuiz();
		}
		// Checking if fields are valid when going back. If not valid, mark step as incomplete
		else if (step === 1) {
			if (!validateStep2()) setCompletedSteps((prev) => prev.filter((step) => step !== 1));
		}

		// If we're going to a page handled by the ref
		if (targetStep >= 2) {
			// Wait for ref to initialize
			if (isMobileLayout && targetStep === 2 && step === 1) setStep(newStep);
			const targetPageName = standaloneAssessmentEditQuizPageRef.current
				? standaloneAssessmentEditQuizPageRef.current.getStepKeyByIndex(targetStep)
				: fixedSteps[targetStep];
			setPendingPageName(targetPageName);
		} else {
			setStep(newStep);
		}
	}

	let modalBody;

	const getNextButtonDisabledState = useCallback(() => {
		if (step === 0) {
			return !title || !dueDate || isNaN(dueDate.getTime()) || dueDate.getTime() < new Date().setHours(0, 0, 0, 0);
		} else if (step === 1) {
			return false;
		}
		return !nextButton.enabled;
	}, [dueDate, nextButton.enabled, step, title]);

	const handleSaveStandaloneQuiz = useCallback(async () => {
		try {
			const saveSuccessful = await standaloneAssessmentEditQuizPageRef.current?.saveButtonHandler();

			if (!saveSuccessful) {
				return;
			}

			if (!savedAssessmentInfo) {
				coreToast.dismiss();
				coreToast.error(i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.indexTSX.addFailed"), {
					autoClose: 2500,
				});
				return;
			}
			const dateWithTime = dueDate ?? new Date();
			dateWithTime?.setHours(23, 59, 59, 999);

			const exemptedStudents = Array.from(uploadedExemptedStudentsMap.values() ?? []).map((exemptedStudent) => ({
				userId: exemptedStudent.userId,
				name: exemptedStudent.name,
				accommodationNotes: exemptedStudent.accommodationNotes,
				proctoringOptionProfileId: exemptedStudent.proctoringOptionProfileId,
			}));

			const userInfo = rows.map((row) => ({
				email: row["Email"].trim(),
				firstName: row["First Name"].trim(),
				lastName: row["Last Name"].trim(),
			}));

			const assessment = await apiClient.assessment.getAssessmentByAssessmentInfo(savedAssessmentInfo);
			if (!assessment) {
				throw new Error("assessment not found");
			}

			// populate the standaloneOptions and create standalone Verity users
			const { standaloneStudentInfo } = await api.saveProctoringAssessmentConfiguration({
				assessmentInfo: savedAssessmentInfo,
				proctoringAssessmentConfiguration: {
					enabled: true,
				},
				standaloneOptions: {
					studentInfoEntries: userInfo,
					instructionTemplate: instruction,
					emailBody: description,
					expiryDate: dateWithTime,
					exemptedEmails: exemptedStudents.map((s) => s.userId.toString()),
					shouldSendInvites: true,
				},
			});
			// create access control users/accounts
			const proctoringOptions = await api.fetchVerityOptionsFromServer(savedAssessmentInfo);
			const currTime = new Date();
			const currDisplayTime = `${currTime.toLocaleDateString([], { dateStyle: "long" })} ${currTime.toLocaleTimeString(
				[],
				{
					hour: "numeric",
					minute: "numeric",
					timeZoneName: "short",
				},
			)}`;
			await Promise.all(
				standaloneStudentInfo?.map(async ({ id: standaloneStudentId, firstName, lastName, email }) => {
					const extensionUrl = Match.value(getBrowserName()).pipe(
						Match.when("firefox", () => import.meta.env.VITE_FIREFOX_EXTENSION_URL),
						Match.orElse(() => import.meta.env.VITE_EXTENSION_URL),
					);
					const { id: accountUuid, userIds } = await apiClient.user.createStandaloneUser({
						instituteUuid,
						courseUuid,
						assessmentUuid: intIdToUuid(assessment.id),
						email,
						firstName,
						lastName,
						expiryDate: dateWithTime,
						emailTemplateData: {
							link: `${window.location.origin}/standalone/login/${standaloneStudentId}`,
							firstName,
							lastName,
							title,
							description,
							currDate: currDisplayTime,
							expiryDate: `${dateWithTime.toLocaleDateString([], { dateStyle: "long" })} ${dateWithTime.toLocaleTimeString(
								[],
								{
									hour: "numeric",
									minute: "numeric",
									timeZoneName: "short",
								},
							)}`,
							lockdownUrl: `${window.location.origin}/download/lockdown.html`,
							extensionUrl,
							isExtension: proctoringOptions.isExtension,
							isLockdown: proctoringOptions.isLockdown,
							isChromeOs: proctoringOptions.isChromeOs,
						},
					});
					const userUuid = userIds[0];
					await apiClient.assessment.linkStandaloneStudentUser({
						assessmentId: assessment.id,
						standaloneStudentId,
						accountUuid,
						userUuid,
					});
				}) ?? [],
			);
			coreToast.dismiss();
			coreToast.success(i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.indexTSX.addSuccess"), {
				autoClose: 2500,
			});
			onFinish();
		} catch (err) {
			console.error(err);
			if (err instanceof ApiClientError) {
				if (err.message.includes("Payload too large")) {
					coreToast.dismiss();
					coreToast.error(
						i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.indexTSX.payloadTooBig"),
						{ autoClose: 2500 },
					);
				}
			} else {
				coreToast.dismiss();
				coreToast.error(i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.indexTSX.addFailed"), {
					autoClose: 2500,
				});
			}
		}
	}, [
		courseUuid,
		description,
		dueDate,
		instituteUuid,
		instruction,
		onFinish,
		rows,
		savedAssessmentInfo,
		title,
		uploadedExemptedStudentsMap,
	]);

	const labelGroupClassName = `${styles.labelGroup} ${shouldShowPreview ? styles.onPreview : ""}`;

	const PreviewSection = () => (
		<div className={styles.rightSide} ref={rightSideRef}>
			<CoreIconButton
				variant="plain"
				className={`${styles.xIcon}`}
				onClick={() => setShouldShowPreview(false)}
				aria-label={i18n.t(
					"portal.components.assessmentPage.createStandAloneQuizDialog.indexTSX.closeButtonPreviewAriaLabel",
				)}
			>
				<XIcon />
			</CoreIconButton>
			<div className={styles.instructionView}>
				<InstructionView instruction={decodeURIComponent(instruction)} title={decodeURIComponent(title)} data={{}} />
			</div>
		</div>
	);

	const assessmentTitle = (
		<Box className={styles.category} sx={{ mt: 3 }}>
			<div className={labelGroupClassName}>
				<div className={styles.fieldName}>
					{i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.indexTSX.quizName")}
				</div>
			</div>
			<CoreTextField
				label={i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.indexTSX.quizNamePrompt")}
				required
				size="small"
				error={titleHelperTest !== "" ? titleHelperTest : undefined}
				value={title}
				onChange={(e) => {
					setTitleHelperTest("");
					setTitle(e.target.value);
				}}
				sx={{ width: "100%" }}
				inputRef={titleRef}
			/>
		</Box>
	);

	const emailInstructions = (
		<Box className={styles.category} sx={{ mt: 2 }}>
			<div className={labelGroupClassName}>
				<div className={styles.fieldName}>
					{i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.indexTSX.emailInstructionsTitle")}
				</div>
				<div className={styles.fieldDescription}>
					{i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.indexTSX.emailInstructions")}
				</div>
			</div>
			<CoreTextField
				label={i18n.t(
					"portal.components.assessmentPage.createStandAloneQuizDialog.indexTSX.emailInstructionsPlaceHolder",
				)}
				size="small"
				multiline
				rows={3}
				value={description}
				onChange={(e) => setDescription(e.target.value)}
				sx={{ width: "100%" }}
			/>
		</Box>
	);

	const assessmentDetails = (
		<Box className={styles.category} sx={{ mt: 2 }}>
			<div className={labelGroupClassName}>
				<div className={styles.fieldName}>
					{i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.indexTSX.assessmentDetails")}
				</div>
				<div className={styles.fieldDescription}>
					{i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.indexTSX.assessmentDetailsDescription")}
				</div>
			</div>

			<div style={{ width: "100%" }}>
				<InstructionEditor
					deltaStaticDefaultValue={delta}
					onChange={(value, _, __, editor) => {
						setInstruction(value);
						setDelta(editor.getContents());
					}}
				/>
			</div>
		</Box>
	);

	const dueDateLabelId = useId();

	const dueDatePicker = (
		<Box className={styles.category} sx={{ mt: 2 }}>
			<div className={labelGroupClassName}>
				<div className={styles.fieldName}>
					{i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.indexTSX.dueDate")}
				</div>
				<div className={styles.fieldDescription} id={dueDateLabelId}>
					{i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.indexTSX.dueDateDescription")}
				</div>
			</div>
			<CoreDatePicker
				value={dueDate}
				onChange={(day) => {
					setDueDate(day);
					validateDueDate(day);
				}}
				dialogAriaLabelledBy={dueDateLabelId}
				ariaLabelledBy={dueDateLabelId}
				disablePast
				inputRef={datePickerRef}
				useButton
				slotProps={{
					textField: {
						sx: {
							"& .MuiInputBase-input": {
								color: "#4D4D4D",
							},
							"& .MuiInputBase-input::placeholder": {
								color: "#4D4D4D",
								textOverflow: "ellipsis",
							},
							width: "180px",
							height: "48px",
							"& .MuiOutlinedInput-root": {
								"& fieldset": {
									border: "1px solid rgba(111, 111, 111, 1)",
								},
							},
						},
						inputProps: {
							required: true,
						},
					},
				}}
				error={datePickerError}
			/>
		</Box>
	);

	const previewButton = (
		<Box className={`${styles.category} ${styles.previewCategory}`} sx={{ mt: 2 }}>
			<div className={labelGroupClassName}>
				<div className={styles.fieldName}>
					{i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.indexTSX.viewPreviewTitle")}
				</div>
				<div className={styles.fieldDescription}>
					{i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.indexTSX.viewPreviewDescription")}
				</div>
			</div>
			<div style={{ width: "100%" }}>
				<CoreButton
					sx={{ color: "#42296e" }}
					aria-expanded={shouldShowPreview}
					onClick={() => {
						setShouldShowPreview(true);
						rightSideRef.current?.scrollIntoView();
					}}
				>
					{i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.indexTSX.viewPreviewButton")}
				</CoreButton>
			</div>
		</Box>
	);

	switch (step) {
		case 0: {
			modalBody = (
				<Box
					component="form"
					noValidate
					autoComplete="off"
					className={styles.modalBody}
					tabIndex={-1}
					ref={modalBodyRef}
				>
					<div className={styles.leftSide} style={{ width: shouldShowPreview ? "49.5%" : "100%" }} ref={leftSideRef}>
						<Box>{i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.indexTSX.titleBox")}</Box>

						{assessmentTitle}

						{emailInstructions}

						{assessmentDetails}

						{dueDatePicker}

						{previewButton}
					</div>

					{shouldShowPreview && <PreviewSection />}
				</Box>
			);
			break;
		}
		case 1: {
			modalBody = (
				<div className={styles.modalBody} ref={modalBodyRef} tabIndex={-1}>
					<Box width="100%">
						<Box sx={{ mt: isMobileLayout ? 0 : 2, mb: 2 }}>
							{i18n.t("portal.components.assessmentPage.createStandAloneQuizDialog.indexTSX.enterInvitees")}
						</Box>

						<InviteeInfoTable
							columns={columns}
							idColumn="Email"
							mandatoryColumns={["Email", "First Name", "Last Name"]}
							rows={rows}
							rowsPerPage={5}
							onColumnsChange={setColumns}
							onRowsChange={setRows}
							shouldShowAddTestTakerTable={shouldShowAddTestTakerTable}
							setShouldShowAddTestTakerTable={setShouldShowAddTestTakerTable}
							isCSVParsed={isCSVParsed}
							setCSVParsed={setCSVParsed}
						/>
					</Box>
				</div>
			);

			break;
		}
		case 2: {
			modalBody = null;
			break;
		}
	}

	const rowsToStudentList = (rows: Record<string, string>[]) => {
		const studentList: { name: string; userId: number }[] = [];
		rows.forEach((row) => {
			const email = row["Email"];

			const userId = email as unknown as number;

			const student = {
				name: email,
				userId: userId,
			};

			studentList.push(student);
		});
		return studentList;
	};

	return (
		// providing the standalone students to the editQuizPage
		<StandAloneExemptedStudentsContext.Provider
			value={{ studentList: rowsToStudentList(rows), exemptedStudents: [], isCreating: true }}
		>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<ThemeWrapper scopedCssBaselineProps={{ className: styles.sectionWrapper }}>
					<div className={styles.contentWrapper}>
						<div className={styles.stepperWrapper}>
							<EditQuizStepper
								activeStep={step}
								steps={standaloneAssessmentEditQuizPageRef.current?.getStepConfig() ?? standAloneStepConfig}
								onStepChange={stepperHandler}
								completedSteps={completedSteps}
							/>
						</div>

						{step < 2 && (
							<CorePaper>
								{modalBody}
								{showErrorDialog && <ErrorModal onClose={() => setShowErrorDialog(false)} />}
							</CorePaper>
						)}
						{savedAssessmentInfo && (
							<div
								style={{
									width: "100%",
									maxWidth: "100%",
									display: step < 2 ? "none" : undefined,
								}}
							>
								<EditAssessmentOptionsPage
									onClose={() => {}}
									assessment={savedAssessmentInfo}
									forceStartOnFirstPage
									standaloneAssessmentEditQuizPageRef={standaloneAssessmentEditQuizPageRef}
									setIsFinalPage={setIsFinalEditQuizPage}
									uploadedExemptedStudentsMap={uploadedExemptedStudentsMap}
									setUploadedExemptedStudentsMap={setUploadedExemptedStudentsMap}
									setEnforcedProfilesOnlyEditQuizPage={setEnforcedProfilesOnlyEditQuizPage}
									quizType="standalone"
									step={step}
									setStep={setStep}
									{...buttonHandlers}
								/>
							</div>
						)}
					</div>
					<div className={styles.buttonWrapper}>
						<EditQuizFooter
							slotProps={{
								back: {
									disabled: step === 0 || (step >= 2 && !backButton.enabled),
									onClick: async () => await handleStepChange(step - 1, "back"),
									hide: backButton.hide,
								},
								next: {
									disabled: getNextButtonDisabledState(),
									onClick: async () => await handleStepChange(step + 1, "next"),
									loading: isSaving,
									hide: isFinalEditQuizPage,
								},
								confirm: {
									disabled: !saveButton.enabled,
									onClick: async () => await handleSaveStandaloneQuiz(),
									hide: !isFinalEditQuizPage,
								},
								saveProfile: {
									onClick: () => standaloneAssessmentEditQuizPageRef?.current?.saveAsProfile(),
									disabled: !saveProfileButton.enabled || enforcedProfilesOnlyEditQuizPage,
									hide: !isFinalEditQuizPage || saveProfileButton.hide,
								},
							}}
						/>
					</div>
				</ThemeWrapper>
			</LocalizationProvider>
		</StandAloneExemptedStudentsContext.Provider>
	);
}
